import React, { createContext, useContext, useState } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';

// When a page renders inside the persistent DashboardShell, this context is set,
// so <DashboardLayout> only renders the page body and the sidebar is NOT remounted
// on every navigation (keeps it identical across all pages).
const ShellContext = createContext(false);

const ShellFrame = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });

  const handleToggleCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('sidebar-collapsed', String(next));
      return next;
    });
  };

  return (
    <div className="relative flex h-screen w-screen overflow-hidden bg-[#f8f8f9] dark:bg-[#0a0a0c] font-sans">
      {/* Sidebar: permanently pinned on the left, does not scroll with main content */}
      <div className="h-full shrink-0 flex flex-col z-30 sticky top-0">
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          isCollapsed={isCollapsed}
          onToggleCollapse={handleToggleCollapse}
        />
      </div>

      {/* Main content inset panel: scrolls independently while sidebar stays pinned */}
      <div
        className="my-0 mx-0 flex min-h-0 min-w-0 flex-1 flex-col h-full overflow-hidden bg-white dark:bg-zinc-900 relative z-10 transition-all duration-300 sm:my-1.5 sm:mr-1.5 sm:rounded-2xl sm:border sm:border-zinc-200 dark:sm:border-zinc-800"
      >
        <Topbar
          onMenuClick={() => setSidebarOpen(true)}
          isCollapsed={isCollapsed}
          onToggleCollapse={handleToggleCollapse}
        />

        <ShellContext.Provider value>
          {children}
        </ShellContext.Provider>
      </div>
    </div>
  );
};

const PageBody = ({ children, noPadding }) => (
  <main
    className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden ${
      noPadding
        ? 'bg-white dark:bg-zinc-900'
        : 'bg-white dark:bg-zinc-950 p-4 sm:p-6 md:p-8'
    }`}
  >
    {children}
  </main>
);

// Persistent layout route element: mounts Sidebar + Topbar once for all dashboard pages.
export const DashboardShell = () => (
  <ShellFrame>
    <Outlet />
  </ShellFrame>
);

const DashboardLayout = ({ children, noPadding = false }) => {
  const insideShell = useContext(ShellContext);

  if (insideShell) {
    return <PageBody noPadding={noPadding}>{children}</PageBody>;
  }

  // Fallback for pages rendered outside the shared shell
  return (
    <ShellFrame>
      <PageBody noPadding={noPadding}>{children}</PageBody>
    </ShellFrame>
  );
};

export default DashboardLayout;
